import {
  CheckCheckIcon,
  ChevronDownIcon,
  EllipsisIcon,
  MailIcon,
  Trash2Icon,
} from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";

import { ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useDeleteRemoveNotifications } from "@/hooks/deleteRemoves/useDeleteRemoveNotifications";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_NOTIFICATION_SURFACES, MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { NotificationResponse, NotificationSelectionFilter } from "@/types";

export const NotificationBulkActions = ({
  notifications,
  selectedIds,
  setSelectedIds,
}: {
  notifications: NotificationResponse[];
  selectedIds: Set<number>;
  setSelectedIds: Dispatch<SetStateAction<Set<number>>>;
}) => {
  const {
    mutate: deleteNotifications,
    isPending: isDeletePending,
    isError: isDeleteError,
  } = useDeleteRemoveNotifications();
  const { mutate: updateNotifications } = useUpdateSetNotifications();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const selectedNotifications = notifications.filter((notification) =>
    selectedIds.has(notification.id),
  );

  const selectedCount = selectedNotifications.length;
  const isAllSelected =
    selectedCount > 0 && selectedCount === notifications.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;
  const hasSelection = selectedCount > 0;

  const hasSelectedUnread = selectedNotifications.some(
    (notification) => notification.readAt === null,
  );
  const hasSelectedRead = selectedNotifications.some(
    (notification) => notification.readAt !== null,
  );

  const applySelectionFilter = (filter: NotificationSelectionFilter) => {
    const matching = notifications.filter((notification) => {
      if (filter === NotificationSelectionFilter.READ) {
        return notification.readAt !== null;
      }
      if (filter === NotificationSelectionFilter.UNREAD) {
        return notification.readAt === null;
      }
      return true;
    });
    setSelectedIds(new Set(matching.map((notification) => notification.id)));
  };

  const handleConfirmDelete = () => {
    mixpanelUtils.trackButtonClick(
      "Bulk delete notifications",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      {
        selected_count: selectedCount,
        surface: MIXPANEL_NOTIFICATION_SURFACES.PAGE,
      },
    );
    deleteNotifications(
      selectedNotifications.map((notification) => notification.id),
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          setIsDeleteDialogOpen(false);
        },
      },
    );
  };

  const handleChangeReadState = (markAsRead: boolean) => {
    mixpanelUtils.trackButtonClick(
      markAsRead
        ? "Bulk mark notifications read"
        : "Bulk mark notifications unread",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      {
        selected_count: selectedCount,
        surface: MIXPANEL_NOTIFICATION_SURFACES.PAGE,
      },
    );
    updateNotifications(
      {
        notificationIds: selectedNotifications.map(
          (notification) => notification.id,
        ),
        markAsRead,
      },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
        },
      },
    );
  };

  return (
    <Row className="items-center gap-1">
      <Checkbox
        checked={isIndeterminate ? "indeterminate" : isAllSelected}
        onCheckedChange={(checked) => {
          if (checked === true) {
            setSelectedIds(
              new Set(notifications.map((notification) => notification.id)),
            );
            return;
          }
          setSelectedIds(new Set());
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="h-8 w-7 px-0" size="sm" variant="ghost">
            <ChevronDownIcon className="h-4 w-4 text-slate-600" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => {
              applySelectionFilter(NotificationSelectionFilter.ALL);
            }}
          >
            All
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              applySelectionFilter(NotificationSelectionFilter.READ);
            }}
          >
            Read
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              applySelectionFilter(NotificationSelectionFilter.UNREAD);
            }}
          >
            Unread
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {hasSelection ? (
        <>
          <Button
            className="h-8 w-8"
            onClick={() => {
              setIsDeleteDialogOpen(true);
            }}
            size="icon"
            variant="ghost"
          >
            <Trash2Icon className="h-4 w-4 text-slate-600" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-8 w-8" size="icon" variant="ghost">
                <EllipsisIcon className="h-4 w-4 text-slate-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {hasSelectedUnread ? (
                <DropdownMenuItem
                  onClick={() => {
                    handleChangeReadState(true);
                  }}
                >
                  <CheckCheckIcon className="h-4 w-4" />
                  Mark as read
                </DropdownMenuItem>
              ) : null}
              {hasSelectedRead ? (
                <DropdownMenuItem
                  onClick={() => {
                    handleChangeReadState(false);
                  }}
                >
                  <MailIcon className="h-4 w-4" />
                  Mark as unread
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : null}
      <ConfirmationDialog
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description={`Are you sure you want to delete ${selectedCount.toString()} notification${selectedCount === 1 ? "" : "s"}? This action cannot be undone.`}
        errorMessage="Failed to delete notifications. Please try again."
        isError={isDeleteError}
        isLoading={isDeletePending}
        onConfirm={handleConfirmDelete}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        title="Delete Notifications"
      />
    </Row>
  );
};
